import {
    Box,
    buildPath,
    buildTemplatePath,
    Card,
    Grid,
    IconButton,
    Page,
    PageRoute,
    Stack,
    Tooltip,
    Typography,
    useElementBreakpoints,
} from "@compass/core";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import AutoStoriesSharpIcon from '@mui/icons-material/AutoStoriesSharp';
import { Home } from "generated/openApi";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { DashboardGrid, SortableCard } from "./components/DashboardGrid";
import { DashboardDataProviders } from "./components/DashboardDataProviders";
import { ManageButton } from "./components/ManageButton";
import { DashboardCardContext, DashboardCardContextValue } from "./components/DashboardCard";
import { CardConfigurationDrawer } from "./components";
import { DashboardCardConfig, CardId } from "./types";
import { CardConfigService } from "./services";
import { DASHBOARD_CARD_REGISTRY } from "./DashboardCardRegistry";
import { ExpandedCardDialog } from "./components/ExpandedCardDialog";


const routeName = "home";

export const homePageRoute: PageRoute = {
    handle: "Home",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

// Wrapper component to memoize context values and prevent unnecessary re-renders
interface CardContextWrapperProps {
    card: DashboardCardConfig;
    onToggle: (cardId: string) => void;
    onExpand: (cardId: CardId) => void;
    children: React.ReactNode;
}

const CardContextWrapper = ({ card, onToggle, onExpand, children }: CardContextWrapperProps) => {
    const value = useMemo<DashboardCardContextValue>(() => ({
        onRemove: card.locked ? undefined : () => onToggle(card.id),
        onExpand: () => onExpand(card.id),
        isExpanded: false,
    }), [card.id, card.locked, onToggle, onExpand]);

    return (
        <DashboardCardContext.Provider value={value}>
            {children}
        </DashboardCardContext.Provider>
    );
};

export const HomePage = () => {
    const { setBreakpoints } = useElementBreakpoints();
    const navigate = useNavigate();

    const [home, setHome] = useState<Home | null>(null);
    const [cardConfigs, setCardConfigs] = useState<DashboardCardConfig[]>([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [expandedCardId, setExpandedCardId] = useState<CardId | null>(null);

    const dashboards = home?.dashboards ?? [];

    const visibleCards = useMemo(
        () => CardConfigService.getVisibleCards(cardConfigs, dashboards),
        [cardConfigs, dashboards]
    );

    const authorizedCards = useMemo(
        () => CardConfigService.filterByAuthorization(cardConfigs, dashboards),
        [cardConfigs, dashboards]
    );

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        ApiService.home.get().then(setHome);
    }, []);

    useEffect(() => {
        const cards = CardConfigService.initialize();
        setCardConfigs(cards.sort((a, b) => a.order - b.order));
    }, []);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const activeId = active.id as CardId;
        const overId = over.id as CardId;

        setCardConfigs((prev) => {
            const dashboards = home?.dashboards ?? [];
            const visible = CardConfigService.getVisibleCards(prev, dashboards);
            const visibleIds: CardId[] = visible.map((c) => c.id);

            const oldIndex = visibleIds.indexOf(activeId);
            const newIndex = visibleIds.indexOf(overId);
            if (oldIndex === -1 || newIndex === -1) return prev;

            const reorderedVisibleIds = arrayMove<CardId>(visibleIds, oldIndex, newIndex);
            const visibleIdSet = new Set<CardId>(reorderedVisibleIds);

            const reorderedVisible = reorderedVisibleIds
                .map((id) => {
                    const match = prev.find((card) => card.id === id);
                    if (!match) {
                        console.warn("No card config found for id:", id);
                    }
                    return match;
                })
                .filter((card): card is DashboardCardConfig => !!card);

            const reorderedAll: DashboardCardConfig[] = [
                ...reorderedVisible,
                ...prev
                    .filter((card) => !visibleIdSet.has(card.id))
                    .sort((a, b) => a.order - b.order),
            ];

            const updated = reorderedAll.map((card, index) => ({
                ...card,
                order: index + 1,
            }));

            CardConfigService.save(updated);
            return updated;
        });
    };

    const handleManageClick = () => setIsDrawerOpen(true);
    const handleDrawerClose = () => setIsDrawerOpen(false);

    // Memoize handlers to maintain stable references for CardContextWrapper
    const handleToggleCard = useCallback((cardId: string) => {
        setCardConfigs((prev) => {
            const updated = CardConfigService.toggleCard(prev, cardId);
            CardConfigService.save(updated);
            return updated;
        });
    }, []);

    const handleExpandCard = useCallback((cardId: CardId) => {
        setExpandedCardId(cardId);
    }, []);

    const handleCloseExpanded = useCallback(() => {
        setExpandedCardId(null);
    }, []);

    return (
        <Page>
            <Stack direction="column" spacing="lg" sx={{ width: '100%' }}>
                <Card>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="column" spacing="sm">
                            <Typography variant="h4">
                                {home ? `Welcome, ${home.userFirstName}` : 'Welcome'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing="md" alignItems="center">
                            <ManageButton onClick={handleManageClick} />
                            <Box>
                            </Box>
                        </Stack>
                    </Stack>
                </Card>

                <Box sx={{ width: "100%" }}>
                    <DashboardDataProviders visibleCardIds={visibleCards.map(c => c.id)}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={visibleCards.map((c) => c.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <DashboardGrid>
                                    {visibleCards.map((card) => {
                                        const CardComponent = DASHBOARD_CARD_REGISTRY[card.id];

                                        if (!CardComponent) {
                                            console.warn(`Card component not found in registry for ID: ${card.id}`);
                                            return null;
                                        }

                                        return (
                                            <Grid
                                                key={card.id}
                                                item
                                                xs={setBreakpoints({ md: 4, xs: 12 })}
                                                sx={{ display: "flex" }}
                                            >
                                                <SortableCard id={card.id}>
                                                    <CardContextWrapper
                                                        card={card}
                                                        onToggle={handleToggleCard}
                                                        onExpand={handleExpandCard}
                                                    >
                                                        <CardComponent />
                                                    </CardContextWrapper>
                                                </SortableCard>
                                            </Grid>
                                        );
                                    })}
                                </DashboardGrid>
                            </SortableContext>
                        </DndContext>

                        <ExpandedCardDialog
                            cardId={expandedCardId}
                            cardConfigs={cardConfigs}
                            onClose={handleCloseExpanded}
                        />
                    </DashboardDataProviders>
                </Box>
            </Stack>

            <CardConfigurationDrawer
                open={isDrawerOpen}
                onClose={handleDrawerClose}
                cards={authorizedCards}
                onToggleCard={handleToggleCard}
            />
        </Page>
    );
};